import { useLocation, useNavigate } from "react-router-dom";
import StopsTable from "./StopsTable";
import NextStopTableScreen from "./NextStopVehicleScreen";
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import config from "../../config.js";
import axios from "axios";
import { CircularProgress, Box, Button } from "@mui/material";
import NavMenu from "../../components/navbar/NavMenu";
import TitleBar from "../../components/navbar/TitleBar";
import MonitorSubtitle from "../../components/common/MonitorSubtitle";
import VehicleDetailsOverlay from "../../components/vehicle/VehicleDetailsOverlay";
import TripSegmentTracker from "./TripSegmentTracker.jsx";
import useCurrentTripStartStopLogs from "../../hooks/useCurrentTripStartStopLogs";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tab,
  Tabs,
} from "@mui/material";
import { reportsApi } from "../../components/manageData/reportsApi.js";
import { formatDate, formatTime } from "../../utils/formatDate.js";
import {
  getISTDateParts,
  getISTNowParts,
  formatISODateIST,
} from "../../utils/istDateUtils";

// Import sub-components
import VehicleHeader from "./vehicleTrack/VehicleHeader";
import {
  StopMarker,
  VehicleMarker,
  StoppedVehicleIndicator,
} from "./vehicleTrack/MapMarkers";
import RoutePolylines from "./vehicleTrack/RoutePolylines";
import { Polyline } from "@react-google-maps/api";
import PreviousTripsTable from "./PreviousTripsTable.jsx";

// Import custom hooks
import { useVehicleTracking } from "./vehicleTrack/useVehicleTracking";
import { useDirections } from "./vehicleTrack/useDirections";
import { usePathDirection } from "./vehicleTrack/usePathDirection";
import { useStopDetails } from "./vehicleTrack/useStopDetails";

// Import utilities
import {
  calculateDistance,
  findNearestPointOnRoute,
  detectVehicleMovement,
  extractPathPoints,
  splitRouteAtPosition,
  checkDirectionChange,
  isJourneyComplete as checkJourneyComplete,
} from "./vehicleTrack/mapUtils";

// Import history point utilities
import {
  saveHistoryPointsToLocalStorage,
  clearHistoryPointsFromLocalStorage,
} from "../../utils/historyPointUtils";

// Import label collision helpers
import {
  computeVehicleLabelOffsets,
  computeStopLabelOffsets,
} from "../../utils/mapLabelOffsets";

// Import constants
import {
  mapContainerStyle,
  mapStyles,
  vehicleInfoBoxStyle,
  mapOptions,
} from "./vehicleTrack/styles";
import { libraries } from "./vehicleTrack/mapConfig";

// Icons
import { useMapIcons } from "../../hooks/useMapIcons";
import { useSettings } from "../../context/SettingsContext";

const DeviceLocationMap = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ─── State ──────────────────────────────────────────────────────────────────
  const [position, setPosition] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [route, setRoute] = useState(null);
  const [stops, setStops] = useState([]);
  const [userState, setUserState] = useState(null);
  const [hasMapPermission, setHasMapPermission] = useState(false);
  const [speed, setSpeed] = useState(null);
  const [directions, setDirections] = useState(null);
  const [stopDetails, setStopDetails] = useState({});
  const [previousPosition, setPreviousPosition] = useState(null);
  const [heading, setHeading] = useState(0);
  const [map, setMap] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [snappedPosition, setSnappedPosition] = useState(null);
  const [routePaths, setRoutePaths] = useState({ before: [], after: [] });
  const [isMoving, setIsMoving] = useState(true);
  const [lastMovedPosition, setLastMovedPosition] = useState(null);
  const [stoppedDuration, setStoppedDuration] = useState(0);
  const [positionHistory, setPositionHistory] = useState([]);
  const [fullPath, setFullPath] = useState([]);
  const [pathDirection, setPathDirection] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [showVehicleDetails, setShowVehicleDetails] = useState(false);
  const [historyPointInterval, setHistoryPointInterval] = useState(30000);
  const [historyPointDistanceInterval, setHistoryPointDistanceInterval] =
    useState(100);
  const [waypointMode, setWaypointMode] = useState("time");

  // ─── Waypoint tracking ──────────────────────────────────────────────────────
  const [waypoints, setWaypoints] = useState([]);
  const [selectedWaypoint, setSelectedWaypoint] = useState(null);
  const [showWaypointTrail, setShowWaypointTrail] = useState(true);
  const [waypointAddresses, setWaypointAddresses] = useState({});
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [movementLogs, setMovementLogs] = useState([]);

  // ─── Historical trips ────────────────────────────────────────────────────────
  const [previousTrips, setPreviousTrips] = useState([]);
  const [selectedHistoricalTrip, setSelectedHistoricalTrip] = useState(null);
  const [historicalPath, setHistoricalPath] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [loadingTripPath, setLoadingTripPath] = useState(false);

  // ─── Bottom tab ──────────────────────────────────────────────────────────────
  const [bottomTab, setBottomTab] = useState(0);

  // ─── Icons ───────────────────────────────────────────────────────────────────
  const { settings } = useSettings();
  const {
    getVehicleIcon,
    getIntermediateStopIcon,
    getOriginStopIcon,
    getDestinationStopIcon,
  } = useMapIcons(
    settings,
    vehicle ? [vehicle] : [],
    stops,
    route ? [route] : [],
  );

  // ─── Refs ────────────────────────────────────────────────────────────────────
  const directionsRef = useRef(directions);
  const directionCheckRef = useRef(false);
  const lastWaypointTimeRef = useRef(null);
  const lastWaypointPositionRef = useRef(null);
  const fetchCurrentTripPathRef = useRef(null);

  const parsedData = JSON.parse(localStorage.getItem("userState"));
  const orgId = parsedData?.user?.organizationId;

  // ─── Current trip start/stop logs (drives history point time window) ─────────
  const { logs: currentTripLogs } = useCurrentTripStartStopLogs(
    vehicle?.device_id,
    orgId,
    30000,
    !!vehicle?.device_id && !!orgId,
  );

  // Earliest timestamp from current trip logs = when this trip started
  const currentTripStartISO = React.useMemo(() => {
    if (!currentTripLogs || currentTripLogs.length === 0) return null;
    const earliest = currentTripLogs
      .map((l) => new Date(l.timestamp).getTime())
      .filter(Boolean)
      .sort((a, b) => a - b)[0];
    return earliest ? new Date(earliest).toISOString() : null;
  }, [currentTripLogs]);

  const hasInitialFitRef = useRef(false);
  const hasPannedToVehicleRef = useRef(false);

  // ─── Track last fetched location for movement detection ──────────────────────
  const lastFetchedLocationRef = useRef(null);
  const lastDataTimestampRef = useRef(null);
  const consecutiveStopCountRef = useRef(0);
  const STOP_THRESHOLD = 4; // 4-5 same coordinates = stop

  // ─── Fetch past trips (last 2 days, not just today) ──────────────────────────
  const fetchTodayTrips = async () => {
    if (!vehicle?.device_id) return [];
    try {
      setLoadingTrips(true);
      const { isoDate: todayISO } = getISTNowParts();
      const [yyy, mmm, ddd] = todayISO.split("-").map(Number);
      const istToday = new Date(yyy, mmm - 1, ddd);
      const istTomorrow = new Date(istToday);
      istTomorrow.setDate(istToday.getDate() + 1);
      const istTwoDaysAgo = new Date(istToday);
      istTwoDaysAgo.setDate(istToday.getDate() - 2);

      const params = {
        orgId,
        siteId: null,
        category: "vehicles",
        selectedIds: [vehicle.device_id],
        selectAll: false,
        dateOption: "custom",
        customStartDate: formatISODateIST(istTwoDaysAgo), // e.g. "2026-03-08"
        customEndDate: formatISODateIST(istTomorrow), // e.g. "2026-03-11"
      };

      const response = await reportsApi.getTripReport(params);
      if (response?.data) {
        const sortedTrips = response.data.sort((a, b) => {
          // Convert any date format to a YYYY-MM-DD comparable string
          const toComparableDate = (dateStr) => {
            if (!dateStr) return "";
            const parts = dateStr.split("/");
            if (parts.length !== 3) return dateStr;
            // DD/MM/YYYY → YYYY-MM-DD
            if (parts[2]?.length === 4)
              return `${parts[2]}-${parts[1]}-${parts[0]}`;
            // YYYY/MM/DD → YYYY-MM-DD
            return `${parts[0]}-${parts[1]}-${parts[2]}`;
          };

          // Compare as plain strings (YYYY-MM-DD HH:MM:SS) — no Date constructor,
          // no timezone ambiguity, identical result on local and deployed server.
          const aKey = `${toComparableDate(a.date)} ${a.startTime || "00:00:00"}`;
          const bKey = `${toComparableDate(b.date)} ${b.startTime || "00:00:00"}`;
          return bKey > aKey ? 1 : bKey < aKey ? -1 : 0; // descending: latest first
        });

        const mapped = sortedTrips.map((trip, idx) => ({
          ...trip,
          id: idx + 1,
        }));
        setPreviousTrips(mapped);
        return mapped; // ← return so callers can use without waiting for state update
      }
      return [];
    } catch (error) {
      console.error("Error fetching trips:", error);
      return [];
    } finally {
      setLoadingTrips(false);
    }
  };

  // ─── Fetch path for a selected historical trip ───────────────────────────────
  const fetchTripPath = async (trip) => {
    if (!trip || !vehicle?.device_id) return;
    try {
      setLoadingTripPath(true);
      setSelectedHistoricalTrip(trip);

      const convertDateFormat = (dateStr) => {
        if (!dateStr) return "";
        // Handle hyphen-separated YYYY-MM-DD (e.g. "2025-08-20")
        if (dateStr.includes("-")) {
          const [year, month, day] = dateStr.split("-");
          return `${day}/${month}/${year}`; // → "20/08/2025"
        }
        // Handle slash-separated
        const parts = dateStr.split("/");
        if (parts.length !== 3) return dateStr;
        // Already DD/MM/YYYY (parts[2] is the 4-digit year)
        if (parts[2]?.length === 4) return dateStr;
        // YYYY/MM/DD → DD/MM/YYYY
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
      };

      const formattedDate = convertDateFormat(trip.date);

      console.log("🔍 Fetching trip path:", {
        originalDate: trip.date,
        formattedDate,
        startTime: trip.startTime,
        endTime: trip.endTime,
        deviceId: vehicle.device_id,
      });

      const response = await axios.post(
        `${config.baseURL}/location/all`,
        { customDates: [] },
        {
          params: {
            deviceIds: vehicle.device_id,
            date: formattedDate,
            startTime: trip.startTime, // already IST — use as-is
            endTime: trip.endTime, // already IST — use as-is
            endDate: "",
            startDate: "",
            selectedDateRange: "today",
          },
          withCredentials: true,
        },
      );

      console.log(
        "✅ Trip path response:",
        response.data?.length || 0,
        "points",
      );

      if (response?.data && response.data.length > 0) {
        const sortedLocations = response.data
          .map((loc) => ({
            lat: parseFloat(loc.latitude),
            lng: parseFloat(loc.longitude),
            timestamp: new Date(loc.createdAt).getTime(),
          }))
          .sort((a, b) => a.timestamp - b.timestamp);

        setHistoricalPath(sortedLocations);

        // if (map && sortedLocations.length > 0) {
        //   const bounds = new window.google.maps.LatLngBounds();
        //   sortedLocations.forEach((pt) =>
        //     bounds.extend({ lat: pt.lat, lng: pt.lng }),
        //   );
        //   map.fitBounds(bounds);
        // }
      } else {
        console.warn("⚠️ No location data returned for trip");
        setHistoricalPath([]);
      }
    } catch (error) {
      console.error("❌ Error fetching trip path:", error);
      setHistoricalPath([]);
    } finally {
      setLoadingTripPath(false);
    }
  };

  const fetchCurrentTripPath = async (currentTripStartISOOverride) => {
    if (!vehicle?.device_id || !route) return;

    // Use the override (passed on first call) or the live hook value
    const tripStartISO = currentTripStartISOOverride ?? currentTripStartISO;

    if (!tripStartISO) {
      console.log(
        "⏳ No current trip start time yet — skipping history point fetch",
      );
      return;
    }

    try {
      // ✅ IST-safe: getISTDateParts / getISTNowParts always return IST values
      // regardless of the server's system timezone (UTC on deployed Linux).
      const tripStartDate = new Date(tripStartISO);
      const { ddmmyyyy: tripDate, time: startTime } =
        getISTDateParts(tripStartDate);
      const { time: currentTime } = getISTNowParts();

      console.log(
        `📍 Fetching history points for current trip: ${tripDate} ${startTime} → ${currentTime}`,
      );

      const response = await axios.post(
        `${config.baseURL}/location/all`,
        { customDates: [] },
        {
          params: {
            deviceIds: vehicle.device_id,
            date: tripDate,
            startTime,
            endTime: currentTime,
            selectedDateRange: "today",
          },
          withCredentials: true,
        },
      );

      if (response?.data && response.data.length > 0) {
        // Filter strictly to points after tripStartISO (belt-and-suspenders)
        const tripStartMs = tripStartDate.getTime();
        const filteredLocationData = response.data.filter((loc) => {
          const ts = new Date(loc.createdAt).getTime();
          return ts >= tripStartMs;
        });

        console.log(
          `✅ Current trip history: ${filteredLocationData.length} / ${response.data.length} points (after ${startTime})`,
        );

        // ── Haversine helper (metres) ─────────────────────────────────────────
        const haversineM = (lat1, lng1, lat2, lng2) => {
          const R = 6371000;
          const dLat = ((lat2 - lat1) * Math.PI) / 180;
          const dLng = ((lng2 - lng1) * Math.PI) / 180;
          const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos((lat1 * Math.PI) / 180) *
              Math.cos((lat2 * Math.PI) / 180) *
              Math.sin(dLng / 2) ** 2;
          return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        };

        // ── Step 1: thin by historyPointInterval (time) ───────────────────────
        // Keep at most one point per interval bucket (e.g. every 30 s).
        const intervalMs = historyPointInterval || 30000;
        const thinned = [];
        let lastKeptTs = -Infinity;
        for (const loc of filteredLocationData) {
          const ts = new Date(loc.createdAt).getTime();
          if (ts - lastKeptTs >= intervalMs) {
            thinned.push(loc);
            lastKeptTs = ts;
          }
        }

        // ── Step 2: thin by historyPointDistanceInterval (distance) ───────────
        // Keep a point only if the vehicle has moved at least the configured
        // distance threshold since the last kept point.
        const distThresholdM = historyPointDistanceInterval || 100;
        const distanceThinned = [];
        for (const loc of thinned) {
          const lat = parseFloat(loc.latitude);
          const lng = parseFloat(loc.longitude);
          if (distanceThinned.length === 0) {
            distanceThinned.push(loc);
            continue;
          }
          const prev = distanceThinned[distanceThinned.length - 1];
          const distM = haversineM(
            parseFloat(prev.latitude),
            parseFloat(prev.longitude),
            lat,
            lng,
          );
          if (distM >= distThresholdM) {
            distanceThinned.push(loc);
          }
        }

        // ── Step 3: deduplicate by position ───────────────────────────────────
        // If several consecutive points share the same coordinate
        // (vehicle stopped), keep only the first one.
        const DEDUP_METERS = 20; // treat points within 20 m as "same location"
        const deduped = [];
        for (const loc of distanceThinned) {
          const lat = parseFloat(loc.latitude);
          const lng = parseFloat(loc.longitude);
          if (deduped.length === 0) {
            deduped.push(loc);
            continue;
          }
          const prev = deduped[deduped.length - 1];
          const distM = haversineM(
            parseFloat(prev.latitude),
            parseFloat(prev.longitude),
            lat,
            lng,
          );
          if (distM > DEDUP_METERS) {
            deduped.push(loc);
          }
        }

        console.log(
          `🗺️ After time-thin (${intervalMs / 1000}s) + dist-thin (${distThresholdM}m) + dedup: ${deduped.length} points`,
        );

        const currentWaypoints = deduped.map((loc) => ({
          id: `waypoint_${loc.id || loc.createdAt}`,
          latitude: parseFloat(loc.latitude),
          longitude: parseFloat(loc.longitude),
          speed: parseFloat(loc.speed) || 0,
          timestamp: new Date(loc.createdAt).getTime(),
          formattedTime: formatTime(new Date(loc.createdAt)),
          formattedDate: formatDate(new Date(loc.createdAt)),
          vehicleId: vehicle.id || location.state,
          vehicleNumber:
            vehicle?.VehicleNumber || vehicle?.vehicleNumber || "N/A",
        }));

        const sortedWaypoints = currentWaypoints.sort(
          (a, b) => a.timestamp - b.timestamp,
        );

        setWaypoints((prev) => {
          const existingTimestamps = new Set(prev.map((w) => w.timestamp));
          const newOnes = sortedWaypoints.filter(
            (w) => !existingTimestamps.has(w.timestamp),
          );
          const merged = [...prev, ...newOnes].sort(
            (a, b) => a.timestamp - b.timestamp,
          );
          if (vehicle?.device_id) {
            saveHistoryPointsToLocalStorage(merged, vehicle.device_id);
          }
          return merged;
        });
      } else {
        console.warn("⚠️ No location history data for current trip");
      }
    } catch (error) {
      console.error("❌ Error fetching current trip path:", error);
    }
  };

  // Keep the ref in sync so polling intervals always call the latest version
  fetchCurrentTripPathRef.current = fetchCurrentTripPath;

  // ─── Clear waypoints whenever the vehicle changes or a new trip starts ───────
  // This ensures we never show history points from a previous trip or day.
  // currentTripStartISO changing means the backend detected a new trip.
  useEffect(() => {
    if (!vehicle?.device_id) return;
    clearHistoryPointsFromLocalStorage(vehicle.device_id);
    setWaypoints([]);
  }, [vehicle?.device_id, currentTripStartISO]);

  // ─── Sequenced effect: fetch history points only for the current trip ─────────
  // currentTripStartISO comes from useCurrentTripStartStopLogs — it is the
  // earliest timestamp in the current trip's START/STOP records, so we never
  // accidentally pull the full day's GPS history.
  useEffect(() => {
    if (!vehicle?.device_id || !route) return;

    let pollInterval = null;
    let cancelled = false;

    const init = async () => {
      // Fetch completed trips for PreviousTripsTable
      fetchTodayTrips();

      if (cancelled) return;

      // Fetch GPS history for the current trip window
      await fetchCurrentTripPath();

      if (cancelled) return;

      // Poll every 30 s for new points.
      // Use the ref so the callback always calls the LATEST fetchCurrentTripPath
      // (avoids stale closure where currentTripStartISO is still null).
      pollInterval = setInterval(() => {
        if (!cancelled) fetchCurrentTripPathRef.current?.();
      }, 30 * 1000);
    };

    init();

    return () => {
      cancelled = true;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [vehicle?.device_id, route, currentTripStartISO]);

  useEffect(() => {
    if (!map || !fullPath?.length || hasInitialFitRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    fullPath.forEach((point) => {
      bounds.extend({ lat: point.lat, lng: point.lng });
    });
    map.fitBounds(bounds, { padding: 60 }); // padding so stops aren't clipped at edges
    hasInitialFitRef.current = true;
  }, [map, fullPath]);

  useEffect(() => {
    if (
      !map ||
      !position ||
      !hasInitialFitRef.current ||
      hasPannedToVehicleRef.current
    )
      return;

    const timer = setTimeout(() => {
      map.panTo(position);
      map.setZoom(16);
      hasPannedToVehicleRef.current = true;
    }, 3000);

    return () => clearTimeout(timer);
  }, [map, position]);

  const handleClearSelectedTrip = () => {
    setSelectedHistoricalTrip(null);
    setHistoricalPath([]);
    if (map && position) {
      map.panTo(position);
      map.setZoom(16);
    }
  };

  // ─── Address lookup (Nominatim) ──────────────────────────────────────────────
  const getAddressFromCoordinates = async (lat, lng) => {
    const cacheKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    if (waypointAddresses[cacheKey]) return waypointAddresses[cacheKey];
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        {
          headers: {
            "User-Agent":
              "RouteEyeVehicleTracking/1.0 (Vehicle Tracking Application)",
          },
        },
      );
      if (!response.ok) throw new Error("Failed to fetch address");
      const data = await response.json();

      let address = "";
      if (data.address) {
        const parts = [];
        if (data.address.road) parts.push(data.address.road);
        if (data.address.neighbourhood) parts.push(data.address.neighbourhood);
        else if (data.address.suburb) parts.push(data.address.suburb);
        if (data.address.city) parts.push(data.address.city);
        else if (data.address.town) parts.push(data.address.town);
        else if (data.address.village) parts.push(data.address.village);
        if (data.address.state) parts.push(data.address.state);
        address = parts.join(", ");
      }
      if (!address && data.display_name)
        address = data.display_name.split(",").slice(0, 4).join(",");
      if (!address) address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

      setWaypointAddresses((prev) => ({ ...prev, [cacheKey]: address }));
      return address;
    } catch (error) {
      console.error("Error fetching address:", error);
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }
  };

  // ─── Haversine helper ────────────────────────────────────────────────────────
  const calculateDistanceInMeters = (lat1, lng1, lat2, lng2) => {
    const R = 6371e3;
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // ─── Fetch address when waypoint selected ────────────────────────────────────
  useEffect(() => {
    const fetchWaypointAddress = async () => {
      if (selectedWaypoint) {
        const cacheKey = `${selectedWaypoint.latitude.toFixed(6)},${selectedWaypoint.longitude.toFixed(6)}`;
        if (!waypointAddresses[cacheKey]) {
          setLoadingAddress(true);
          await getAddressFromCoordinates(
            selectedWaypoint.latitude,
            selectedWaypoint.longitude,
          );
          setLoadingAddress(false);
        }
      }
    };
    fetchWaypointAddress();
  }, [selectedWaypoint]);

  // ─── Fetch app settings ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAppSettings = async () => {
      try {
        const response = await axios.get(
          `${config.baseURL}/appSettings/organization`,
          { params: { organizationId: orgId }, withCredentials: true },
        );
        if (response.data?.historyPointInterval) {
          const intervalMs = response.data.historyPointInterval * 1000;
          setHistoryPointInterval(intervalMs);
          console.log("✅ Fetched historyPointInterval from API:", intervalMs);
        } else {
          const local = JSON.parse(localStorage.getItem("appSettings"));
          setHistoryPointInterval((local?.historyPointInterval || 30) * 1000);
          console.log("⚠️ Using historyPointInterval from localStorage");
        }
        if (response.data?.historyPointDistanceInterval) {
          setHistoryPointDistanceInterval(
            response.data.historyPointDistanceInterval,
          );
          console.log(
            "✅ Fetched historyPointDistanceInterval from API:",
            response.data.historyPointDistanceInterval,
            "m",
          );
        } else {
          const local = JSON.parse(localStorage.getItem("appSettings"));
          setHistoryPointDistanceInterval(
            local?.historyPointDistanceInterval || 100,
          );
          console.log(
            "⚠️ Using historyPointDistanceInterval from localStorage",
          );
        }
      } catch (error) {
        console.error("❌ Error fetching app settings:", error);
        const local = JSON.parse(localStorage.getItem("appSettings"));
        setHistoryPointInterval((local?.historyPointInterval || 30) * 1000);
        setHistoryPointDistanceInterval(
          local?.historyPointDistanceInterval || 100,
        );
        console.log("⚠️ Fallback to localStorage due to API error");
      }
    };
    if (orgId) fetchAppSettings();
  }, [orgId]);

  // ─── Clear waypoints on direction change ─────────────────────────────────────
  useEffect(() => {
    if (pathDirection) {
      console.log("🧭 Path direction changed to:", pathDirection);
      console.log("🗑️ Clearing waypoints for completed trip");
      // Clear from state
      setWaypoints([]);
      setMovementLogs([]);
      setSelectedWaypoint(null);
      setWaypointAddresses({});
      lastWaypointTimeRef.current = null;
      lastWaypointPositionRef.current = null;
      // Also clear from localStorage when trip completes
      if (vehicle?.device_id) {
        clearHistoryPointsFromLocalStorage(vehicle.device_id);
        console.log(
          `🗑️ Cleared history points from localStorage for vehicle ${vehicle.device_id}`,
        );
      }
    }
  }, [pathDirection, vehicle?.device_id]);

  // ─── Global clear callback for TripSegmentTracker ────────────────────────────
  useEffect(() => {
    window.clearWaypointsForTrip = () => {
      console.log("🗑️ Clearing waypoints via global callback");
      setWaypoints([]);
      setMovementLogs([]);
      setSelectedWaypoint(null);
      setWaypointAddresses({});
      lastWaypointTimeRef.current = null;
      lastWaypointPositionRef.current = null;
    };
    return () => {
      delete window.clearWaypointsForTrip;
    };
  }, []);

  useEffect(() => {
    directionsRef.current = directions;
  }, [directions]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const onLoad = useCallback((map) => setMap(map), []);
  const onUnmount = useCallback(() => setMap(null), []);

  // ─── User permissions ────────────────────────────────────────────────────────
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userState"));
    if (user) {
      setUserState(user);
      setHasMapPermission(
        user.features.some(
          (f) => f.api_path === "/api/stop/organization" && f.action === "GET",
        ),
      );
    }
  }, []);

  // ─── Fetch vehicle + route + stops ──────────────────────────────────────────
  const fetchVehicleData = async () => {
    if (!location.state) return;
    try {
      const response = await axios.get(`${config.baseURL}/vehicle/id`, {
        params: { vehicleId: location.state, organizationId: orgId },
        withCredentials: true,
      });
      const vehicleData = response.data.vehicle;
      console.log("Fetched vehicle data:", vehicleData);
      setVehicle(vehicleData);

      const routeData = await axios.get(`${config.baseURL}/route/id`, {
        params: { id: vehicleData.routeId },
        withCredentials: true,
      });
      if (routeData.data) {
        setRoute(routeData.data);
        setStops(await fetchStopsForRoute(routeData.data));
      }
    } catch (error) {
      console.error("Error fetching vehicle data:", error);
    }
  };

  const fetchStopsForRoute = async (route) => {
    if (!route?.routeCoordinates) return [];
    const stops = [];
    for (const coordinate of route.routeCoordinates) {
      try {
        const response = await axios.get(
          `${config.baseURL}/stop/organization/location`,
          {
            params: {
              organizationId: orgId,
              longitude: coordinate.longitude,
              latitude: coordinate.latitude,
            },
            withCredentials: true,
          },
        );
        stops.push({
          stopId: response.data.stopId || response.data.id,
          name: response.data.name,
          latitude: response.data.latitude,
          longitude: response.data.longitude,
        });
      } catch (error) {
        console.error("Error fetching stops:", error);
      }
    }
    return stops;
  };

  useEffect(() => {
    fetchVehicleData();
    const intervalId = setInterval(fetchVehicleData, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [location.state, orgId]);

  // ─── Visible stops (direction-aware) ────────────────────────────────────────
  const visibleStops = useMemo(() => {
    return stops.filter((stop) => {
      if (pathDirection === "source_to_dest")
        return stop.name !== "Manipal Hospital";
      if (pathDirection === "dest_to_source")
        return stop.name !== "BIAL-T1-Departure";
      return true;
    });
  }, [stops, pathDirection]);

  // ─── Label collision offsets ──────────────────────────────────────────────────
  // Stop labels: push overlapping labels upward so they don't pile up.
  const stopLabelOffsets = useMemo(() => {
    if (!map || !visibleStops.length) return {};
    const projection = map.getProjection();
    if (!projection) return {};
    const entries = visibleStops.map((s) => ({
      id: s.id,
      lat: parseFloat(s.latitude),
      lng: parseFloat(s.longitude),
      name: s.name,
    }));
    return computeStopLabelOffsets(projection, map.getZoom() ?? 16, entries, {
      BASE_OFFSET: 44,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, visibleStops, zoom]);

  // Vehicle label: single vehicle but still collision-aware (handles future multi-vehicle extension).
  const vehicleLabelOffset = useMemo(() => {
    if (!map || !position) return {};
    const projection = map.getProjection();
    if (!projection) return {};
    const entries = [
      {
        deviceId: vehicle?.device_id ?? "vehicle",
        lat: position.lat,
        lng: position.lng,
        name: vehicle?.name || "Vehicle",
        hasArrow: false,
      },
    ];
    return computeVehicleLabelOffsets(
      projection,
      map.getZoom() ?? 16,
      entries,
      {
        ANCHOR_SHIFT: 13,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, position, zoom]);

  // ─── Waypoint recording (time + distance modes) ──────────────────────────────
  // useEffect(() => {
  //   if (!position || !isMoving || !vehicle) return;

  //   const now = Date.now();
  //   let shouldRecord = false;

  //   if (waypointMode === "time") {
  //     if (
  //       !lastWaypointTimeRef.current ||
  //       now - lastWaypointTimeRef.current >= historyPointInterval
  //     ) {
  //       shouldRecord = true;
  //     }
  //   } else if (waypointMode === "distance") {
  //     if (!lastWaypointPositionRef.current) {
  //       shouldRecord = true;
  //     } else {
  //       const dist = calculateDistanceInMeters(
  //         lastWaypointPositionRef.current.lat,
  //         lastWaypointPositionRef.current.lng,
  //         position.lat,
  //         position.lng,
  //       );
  //       if (dist >= DISTANCE_THRESHOLD) shouldRecord = true;
  //     }
  //   }

  //   if (shouldRecord) {
  //     const waypoint = {
  //       id: `waypoint_${now}`,
  //       latitude: position.lat,
  //       longitude: position.lng,
  //       speed: parseFloat(speed) || 0,
  //       timestamp: now,
  //       formattedTime: formatTime(new Date(now)),
  //       formattedDate: formatDate(new Date(now)),
  //       vehicleId: vehicle.id || location.state,
  //       vehicleNumber:
  //         vehicle?.VehicleNumber || vehicle?.vehicleNumber || "N/A",
  //     };

  //     setWaypoints((prev) => [...prev, waypoint]);

  //     lastWaypointTimeRef.current = now;
  //     lastWaypointPositionRef.current = {
  //       lat: position.lat,
  //       lng: position.lng,
  //     };
  //     console.log(
  //       `📍 Waypoint recorded (${waypointMode} mode):`,
  //       waypoint,
  //       "Speed:",
  //       speed,
  //     );
  //   }
  // }, [
  //   position,
  //   isMoving,
  //   speed,
  //   vehicle,
  //   location.state,
  //   historyPointInterval,
  //   waypointMode,
  // ]);

  // ─── Custom hooks ────────────────────────────────────────────────────────────
  useVehicleTracking({
    vehicle,
    position,
    heading,
    lastMovedPosition,
    findNearestPointOnRoute,
    detectVehicleMovement,
    calculateDistance,
    directionsRef,
    map,
    setPosition,
    setHeading,
    setSpeed,
    setIsMoving,
    setLastMovedPosition,
    setStoppedDuration,
    setSnappedPosition,
    setPreviousPosition,
    setPositionHistory,
    setIsOnline,
    setIsReconnecting,
  });

  useDirections({
    isLoaded,
    stops,
    route,
    position,
    map,
    setDirections,
    setFullPath,
    extractPathPoints,
  });

  usePathDirection({
    vehicle,
    positionHistory,
    pathDirection,
    stops,
    calculateDistance,
    checkDirectionChange,
    checkJourneyComplete,
    setPathDirection,
    setPositionHistory,
    directionCheckRef,
  });

  useStopDetails({ stops: visibleStops, vehicle, setStopDetails });

  // ─── Split route at snapped position ─────────────────────────────────────────
  useEffect(() => {
    if (directions && snappedPosition) {
      const allPoints = extractPathPoints(directions);
      setRoutePaths(
        splitRouteAtPosition(
          allPoints,
          snappedPosition,
          pathDirection,
          calculateDistance,
        ),
      );
    }
  }, [directions, snappedPosition, pathDirection]);

  // ─── Navigation ──────────────────────────────────────────────────────────────
  const handleVehicleView = () =>
    navigate(`/vehicle/screen/${location.state}`, {
      state: { vehicleId: location.state, organizationId: orgId },
    });

  const handleDriverView = () =>
    navigate({
      pathname: "/console",
      search: `?routeId=${vehicle?.routeId}&organizationId=${orgId}&vehicleId=${location.state}`,
    });

  const handleClearWaypoints = () => {
    setWaypoints([]);
    setSelectedWaypoint(null);
    setWaypointAddresses({});
    lastWaypointTimeRef.current = null;
    lastWaypointPositionRef.current = null;
  };

  console.log(selectedWaypoint);

  if (loadError) {
    return (
      <div>
        <TitleBar />
        <NavMenu />
        <div style={{ padding: "32px" }}>
          Error loading maps. Please try again later.
        </div>
      </div>
    );
  }

  const orgName = parsedData?.organization?.name;

  return (
    <>
      <TitleBar />
      <NavMenu />
      <MonitorSubtitle
        title="Monitor Vehicle"
        path={`${orgName || "organization"} > Vehicles > ${
          vehicle?.name || "Monitor Vehicle"
        } > Monitor`}
        track="Vehicle View"
        add="Driver View"
        onTrackClick={handleVehicleView}
        onAddClick={handleDriverView}
      />
      <div
        style={{
          padding: "0px 40px",
          width: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
        }}
      >
        {hasMapPermission && (
          <>
            {!isLoaded ? (
              <div
                style={{
                  height: "68.5vh",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  alignItems: "stretch",
                  marginBottom: "20px",
                  width: "100%",
                  boxSizing: "border-box",
                  position: "relative",
                  zIndex: 0,
                }}
              >
                <Box
                  sx={{
                    ...vehicleInfoBoxStyle,
                    position: "relative",
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <VehicleHeader vehicle={vehicle} route={route} />

                  <div
                    style={{
                      display: "flex",
                      minWidth: 0,
                      overflow: "hidden",
                      gap: "20px",
                      marginTop: "20px",
                    }}
                  >
                    {/* ── Main Map ── */}
                    <div
                      style={{
                        position: "relative",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Trail toggle + mode selector */}
                      <div
                        style={{
                          position: "absolute",
                          top: "30px",
                          left: "10px",
                          zIndex: 100,
                          display: "flex",
                          gap: "10px",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() =>
                            setShowWaypointTrail(!showWaypointTrail)
                          }
                          sx={{
                            backgroundColor: "#1e3a5f",
                            color: "white",
                            textTransform: "none",
                            fontSize: "13px",
                            fontWeight: 500,
                            padding: "6px 14px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                            "&:hover": { backgroundColor: "#2c5282" },
                          }}
                        >
                          {showWaypointTrail
                            ? "Hide Vehicle Trail"
                            : "Show Vehicle Trail"}
                        </Button>

                        {/* <FormControl
                          size="small"
                          sx={{
                            backgroundColor: "white",
                            borderRadius: "4px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                          }}
                        >
                          <Select
                            value={waypointMode}
                            onChange={(e) => setWaypointMode(e.target.value)}
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                { border: "none" },
                            }}
                          >
                            <MenuItem value="time" sx={{ fontSize: "13px" }}>
                              Time Based
                            </MenuItem>
                            <MenuItem
                              value="distance"
                              sx={{ fontSize: "13px" }}
                            >
                              Distance Based
                            </MenuItem>
                          </Select>
                        </FormControl> */}
                      </div>

                      <GoogleMap
                        mapContainerStyle={{
                          ...mapContainerStyle,
                          height: "100%",
                          flex: 1,
                        }}
                        center={
                          position ||
                          (stops?.length
                            ? {
                                lat: parseFloat(stops[0].latitude),
                                lng: parseFloat(stops[0].longitude),
                              }
                            : null)
                        }
                        zoom={16}
                        onLoad={onLoad}
                        onUnmount={onUnmount}
                        options={{ ...mapOptions, styles: mapStyles }}
                        onZoomChanged={() => {
                          if (map) setZoom(map.getZoom());
                        }}
                      >
                        <RoutePolylines
                          fullPath={fullPath}
                          pathDirection={pathDirection}
                          routePaths={routePaths}
                          routeColor={route?.routeColor}
                        />

                        <StoppedVehicleIndicator
                          position={position}
                          stoppedDuration={stoppedDuration}
                          isMoving={isMoving}
                        />

                        {visibleStops.map((stop, index) => (
                          <StopMarker
                            key={`stop-${stop.id}-${index}`}
                            stop={stop}
                            index={index}
                            stopsLength={visibleStops.length}
                            getOriginIcon={getOriginStopIcon}
                            getDestinationIcon={getDestinationStopIcon}
                            getIntermediateStopIcon={getIntermediateStopIcon}
                            route={route ? [route] : []}
                            labelOffsetPx={
                              stopLabelOffsets[stop.id]?.topPx ?? 44
                            }
                          />
                        ))}

                        {/* Historical trip path */}
                        {/* {historicalPath.length > 0 && (
                          <>
                            <Polyline
                              path={historicalPath}
                              options={{
                                strokeColor: "#FF6B00",
                                strokeOpacity: 0.8,
                                strokeWeight: 5,
                                zIndex: 1000,
                              }}
                            />
                            <Marker
                              position={historicalPath[0]}
                              icon={{
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: 8,
                                fillColor: "#00FF00",
                                fillOpacity: 1,
                                strokeColor: "#FFFFFF",
                                strokeWeight: 2,
                              }}
                              title="Trip Start"
                            />
                            <Marker
                              position={
                                historicalPath[historicalPath.length - 1]
                              }
                              icon={{
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: 8,
                                fillColor: "#FF0000",
                                fillOpacity: 1,
                                strokeColor: "#FFFFFF",
                                strokeWeight: 2,
                              }}
                              title="Trip End"
                            />
                          </>
                        )} */}

                        {/* Live waypoint trail */}
                        {showWaypointTrail &&
                          waypoints.map((waypoint) => (
                            <Marker
                              key={waypoint.id}
                              position={{
                                lat: parseFloat(waypoint.latitude),
                                lng: parseFloat(waypoint.longitude),
                              }}
                              onClick={() => setSelectedWaypoint(waypoint)}
                              icon={{
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: 5,
                                fillColor: "#4285F4",
                                fillOpacity: 0.9,
                                strokeColor: "#FFFFFF",
                                strokeWeight: 2,
                              }}
                            />
                          ))}

                        {/* Waypoint InfoWindow */}
                        {showWaypointTrail && selectedWaypoint && (
                          <InfoWindow
                            position={{
                              lat: parseFloat(selectedWaypoint.latitude),
                              lng: parseFloat(selectedWaypoint.longitude),
                            }}
                            onCloseClick={() => setSelectedWaypoint(null)}
                            options={{
                              pixelOffset: new window.google.maps.Size(0, -10),
                            }}
                          >
                            <div
                              style={{
                                padding: "0",
                                minWidth: "280px",
                                fontFamily:
                                  "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                              }}
                            >
                              <div
                                style={{
                                  borderBottom: "1px solid #e0e0e0",
                                  paddingBottom: "8px",
                                  marginBottom: "10px",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#666",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {selectedWaypoint.formattedTime}
                                </div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#1a73e8",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {selectedWaypoint.vehicleNumber
                                    ? `Vehicle-${selectedWaypoint.vehicleId} (${selectedWaypoint.vehicleNumber})`
                                    : `Vehicle-${selectedWaypoint.vehicleId}`}
                                </div>
                                <div
                                  style={{ fontSize: "12px", color: "#666" }}
                                >
                                  Date: {selectedWaypoint.formattedDate}
                                </div>
                              </div>

                              <div
                                style={{
                                  backgroundColor: "#34a853",
                                  color: "white",
                                  padding: "8px 12px",
                                  borderRadius: "4px",
                                  textAlign: "center",
                                  marginBottom: "12px",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                }}
                              >
                                Speed: {selectedWaypoint.speed.toFixed(2)} km/h
                              </div>

                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#666",
                                  display: "flex",
                                  gap: "4px",
                                  alignItems: "flex-start",
                                  paddingTop: "8px",
                                  borderTop: "1px solid #e0e0e0",
                                }}
                              >
                                <span
                                  style={{ color: "#ea4335", marginTop: "2px" }}
                                >
                                  📍
                                </span>
                                <div style={{ flex: 1 }}>
                                  {loadingAddress ? (
                                    <span
                                      style={{
                                        color: "#999",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      Loading address...
                                    </span>
                                  ) : (
                                    <div
                                      style={{
                                        lineHeight: "1.5",
                                        color: "#333",
                                        fontSize: "11px",
                                      }}
                                    >
                                      {waypointAddresses[
                                        `${selectedWaypoint.latitude.toFixed(6)},${selectedWaypoint.longitude.toFixed(6)}`
                                      ] ||
                                        `Latitude: ${parseFloat(selectedWaypoint.latitude).toFixed(6)}, Longitude: ${parseFloat(selectedWaypoint.longitude).toFixed(6)}`}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </InfoWindow>
                        )}

                        <VehicleMarker
                          position={position}
                          vehicle={vehicle}
                          isMoving={isMoving}
                          heading={heading}
                          pathDirection={pathDirection}
                          onMarkerClick={() =>
                            setShowVehicleDetails((prev) => !prev)
                          }
                          getVehicleIcon={getVehicleIcon}
                          labelOffsetPx={
                            vehicleLabelOffset[vehicle?.device_id ?? "vehicle"]
                              ?.topPx ?? 10
                          }
                        />

                        {showVehicleDetails && position && (
                          <VehicleDetailsOverlay
                            vehicle={vehicle}
                            route={route}
                            isMoving={isMoving}
                            stoppedDuration={stoppedDuration}
                            position={position}
                          />
                        )}
                      </GoogleMap>
                    </div>

                    {/* ── Stops sidebar ── */}
                    <div
                      style={{
                        flex: "0 0 35%",
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                      }}
                    >
                      {!stops ? (
                        <div style={{ textAlign: "center" }}>
                          <CircularProgress />
                        </div>
                      ) : (
                        <div style={{ height: "auto" }}>
                          <StopsTable
                            stops={visibleStops}
                            stopDetails={stopDetails}
                            vehiclePosition={snappedPosition}
                            isMoving={isMoving}
                            stoppedDuration={stoppedDuration}
                            vehicleId={location.state}
                            pathDirection={pathDirection}
                            routeCoordinates={route?.routeCoordinates}
                            isInbound={pathDirection === "dest_to_source"}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── Bottom tabs: Movement Log + Today's Trips ── */}
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
               
                      backgroundColor: "#FFFFFF",
                      borderRadius: "10px",
                      border: "1px solid #A5DCF3",
                      boxShadow: "-2px 1px 5px 1px #00000040",
                    }}
                  >
                    <Tabs
                      value={bottomTab}
                      onChange={(_, newVal) => setBottomTab(newVal)}
                      variant="fullWidth"
                      sx={{
                        mb: 2,
                        "& .MuiTabs-flexContainer": { gap: "0px" },
                        "& .MuiTab-root": {
                          textTransform: "none",
                          fontSize: "16px",
                          fontFamily: "Roboto",
                          fontWeight: 500,
                          minHeight: "48px",
                          border: "1px solid #95A0B3",
                          color: "#666666",
                          backgroundColor: "#FFFFFF",
                          "&.Mui-selected": {
                            backgroundColor: "#20A8E0",
                            color: "#FFFFFF",
                          },
                          "&:first-of-type": {
                            borderTopLeftRadius: "8px",
                            borderBottomLeftRadius: "8px",
                          },
                          "&:last-of-type": {
                            borderTopRightRadius: "8px",
                            borderBottomRightRadius: "8px",
                          },
                          borderRight: "none",
                          "&:last-child": { borderRight: "1px solid #95A0B3" },
                        },
                        "& .MuiTabs-indicator": { display: "none" },
                      }}
                    >
                      <Tab label="Current Trip" />
                      <Tab label="Past Trips" />
                    </Tabs>

                    <Box sx={{ display: bottomTab === 0 ? "block" : "none" }}>
                      <TripSegmentTracker
                        vehiclePosition={position}
                        isMoving={isMoving}
                        stoppedDuration={stoppedDuration}
                        vehicleId={vehicle?.device_id}
                        pathDirection={pathDirection}
                        vehicleNumber={
                          vehicle?.VehicleNumber || vehicle?.vehicleNumber
                        }
                        movementLogs={movementLogs}
                        waypoints={waypoints}
                        stops={stops}
                      />
                    </Box>

                    <Box sx={{ display: bottomTab === 1 ? "block" : "none" }}>
                      <PreviousTripsTable
                        trips={previousTrips}
                        loading={loadingTrips}
                        selectedTrip={selectedHistoricalTrip}
                        onTripClick={fetchTripPath}
                        onClearSelection={handleClearSelectedTrip}
                        loadingPath={loadingTripPath}
                        historicalPath={historicalPath}
                        isLoaded={isLoaded}
                        historyPointInterval={historyPointInterval}
                        vehicle={vehicle}
                        route={route}
                        mapStyles={mapStyles}
                        mapOptions={mapOptions}
                      />
                    </Box>
                  </Box>
                </Box>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default DeviceLocationMap;
